import type {
    BaseRecord,
    CreateParams,
    CreateResponse,
    CustomParams,
    CustomResponse,
    DataProvider,
    DeleteOneParams,
    DeleteOneResponse,
    GetListParams,
    GetListResponse,
    GetOneParams,
    GetOneResponse,
    UpdateParams,
    UpdateResponse
} from "@refinedev/core"
import {DOMAIN_API} from "../../utils/api"
import {store} from "../../features/store"
import {applyAdminRequestHeaders} from "../../utils/apiHeaders"
import {getApiErrorMessage} from "../../utils/apiError"
import {translateUi} from "../../i18n/translateUi"
import {isUnsecureUrl} from "../../utils/unsecureEndpoints"

const resolveUrl = (path: string) => {
    if (/^https?:\/\//.test(path)) return path

    const normalized = path.replace(/^\/+/, "")
    return `${DOMAIN_API}/${normalized}`
}

const withQuery = (url: string, params?: Record<string, string | number | undefined>) => {
    if (!params) return url

    const search = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
            search.set(key, String(value))
        }
    })

    const query = search.toString()
    return query ? `${url}?${query}` : url
}

const parseResponse = async <T>(response: Response): Promise<T> => {
    const text = await response.text()
    let json: unknown = null

    if (text) {
        try {
            json = JSON.parse(text)
        } catch {
            const error = new Error(
                response.ok
                    ? translateUi("Сервер вернул ответ в неизвестном формате")
                    : text || response.statusText || "Request error"
            ) as Error & {statusCode?: number; details?: unknown}
            error.statusCode = response.status
            error.details = text
            throw error
        }
    }

    if (!response.ok) {
        const message = getApiErrorMessage(json, response.statusText || "Request error")
        const error = new Error(message) as Error & {statusCode?: number; details?: unknown}
        error.statusCode = response.status
        error.details = json
        throw error
    }

    return json as T
}

const getAuthHeaders = (url?: string, headers?: HeadersInit, hasBody = false) => {
    const parsedHeaders = new Headers(headers)
    const token = store.getState().auth.token

    if (token && !isUnsecureUrl(url, DOMAIN_API)) {
        parsedHeaders.set("Authorization", `Bearer ${token}`)
    }

    if (hasBody && !parsedHeaders.has("Content-Type")) {
        parsedHeaders.set("Content-Type", "application/json")
    }

    return applyAdminRequestHeaders(parsedHeaders)
}

const unwrapData = <T>(payload: unknown): T => {
    if (payload && typeof payload === "object" && "data" in payload) {
        return (payload as {data: T}).data
    }

    return payload as T
}

export const dataProvider: DataProvider = {
    getApiUrl: () => DOMAIN_API,
    getList: async <TData extends BaseRecord = BaseRecord>({
        resource,
        pagination
    }: GetListParams): Promise<GetListResponse<TData>> => {
        const url = withQuery(resolveUrl(resource), {
            page: pagination?.current,
            size: pagination?.pageSize
        })
        const response = await fetch(url, {
            method: "GET",
            headers: getAuthHeaders(url)
        })
        const payload = unwrapData<TData[] | {
            items?: TData[]
            list?: TData[]
            total?: number
            count?: number
        }>(await parseResponse<unknown>(response))
        const data = Array.isArray(payload) ? payload : payload.items || payload.list || []
        const total = Array.isArray(payload) ? data.length : payload.total || payload.count || data.length

        return {
            data,
            total
        }
    },
    getOne: async <TData extends BaseRecord = BaseRecord>({
        resource,
        id
    }: GetOneParams): Promise<GetOneResponse<TData>> => {
        const url = resolveUrl(`${resource}/${id}`)
        const response = await fetch(url, {
            method: "GET",
            headers: getAuthHeaders(url)
        })
        const payload = unwrapData<TData>(await parseResponse<unknown>(response))

        return {
            data: payload
        }
    },
    create: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
        resource,
        variables
    }: CreateParams<TVariables>): Promise<CreateResponse<TData>> => {
        const url = resolveUrl(resource)
        const response = await fetch(url, {
            method: "POST",
            headers: getAuthHeaders(url, undefined, true),
            body: JSON.stringify(variables || {})
        })
        const payload = unwrapData<TData>(await parseResponse<unknown>(response))

        return {
            data: payload
        }
    },
    update: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
        resource,
        id,
        variables
    }: UpdateParams<TVariables>): Promise<UpdateResponse<TData>> => {
        const url = resolveUrl(`${resource}/${id}`)
        const response = await fetch(url, {
            method: "PATCH",
            headers: getAuthHeaders(url, undefined, true),
            body: JSON.stringify(variables || {})
        })
        const payload = unwrapData<TData>(await parseResponse<unknown>(response))

        return {
            data: payload
        }
    },
    deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
        resource,
        id,
        variables
    }: DeleteOneParams<TVariables>): Promise<DeleteOneResponse<TData>> => {
        const url = resolveUrl(`${resource}/${id}`)
        const response = await fetch(url, {
            method: "DELETE",
            headers: getAuthHeaders(url, undefined, Boolean(variables)),
            ...(variables ? {body: JSON.stringify(variables)} : {})
        })
        const payload = unwrapData<TData | null>(await parseResponse<unknown>(response))

        return {
            data: payload || ({id} as TData)
        }
    },
    custom: async <TData extends BaseRecord = BaseRecord, TQuery = unknown, TPayload = unknown>({
        url,
        method,
        payload,
        query,
        headers
    }: CustomParams<TQuery, TPayload>): Promise<CustomResponse<TData>> => {
        const requestUrl = withQuery(resolveUrl(url), query as Record<string, string | number | undefined> | undefined)
        const hasBody = payload !== undefined
        const response = await fetch(requestUrl, {
            method: method || "GET",
            headers: getAuthHeaders(requestUrl, headers, hasBody),
            ...(hasBody ? {body: JSON.stringify(payload)} : {})
        })
        const parsed = await parseResponse<unknown>(response)
        const data = unwrapData<TData>(parsed)

        return {
            data
        }
    }
}

